import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	firstValueFrom,
	Subject,
	Subscription,
	takeUntil,
} from 'rxjs';
import { UploadApi } from './upload-api';
import { ServiceNotification } from '@shared/services/notification/service-notification';
import { runWithConcurrency } from '@shared/utils/concurrency-pool';
import { UserSessionService } from '@core/services/session/user-session.service';
import { filtrarImagensVooRapido, logVooRapido } from './voo-rapido';

export type UploadStatus =
	| 'idle'
	| 'uploading'
	| 'processing'
	| 'done'
	| 'error'
	| 'cancelled';

export interface UploadState {
	status: UploadStatus;
	progress: number;
	totalFiles: number;
	completedFiles: number;
	bucketName?: string;
	subBucketNames?: string[];
	errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class UploadManagerService {
	private readonly toastKey = 'global';

	private readonly FOLDER_UPLOAD_CONCURRENCY = 8;

	private readonly stateSubject = new BehaviorSubject<UploadState>({
		status: 'idle',
		progress: 0,
		totalFiles: 0,
		completedFiles: 0,
	});
	readonly state$ = this.stateSubject.asObservable();

	private cancelled = false;
	private activeSubs = new Set<Subscription>();
	private cancelSignal$ = new Subject<void>();

	private isSubmitting = false;

	constructor(
		private uploadService: UploadApi,
		private notification: ServiceNotification,
		private userSession: UserSessionService,
	) {}

	getState(): UploadState {
		return this.stateSubject.value;
	}

	async startUploadFolder(
		files: FileList | null,
		dataLevantamento?: string | null,
		modoDev?: boolean,
		vooRapido?: boolean,
	): Promise<void> {
		if (!files || files.length === 0) return;
		if (this.isSubmitting) return;

		this.isSubmitting = true;

		const uploadStartMs = Date.now();
		this.cancelled = false;
		this.cancelSignal$.next();
		this.cancelSignal$.complete();
		this.cancelSignal$ = new Subject<void>();
		this.cleanupSubs();

		let fileArray = Array.from(files);
		if (vooRapido) {
			const resultado = filtrarImagensVooRapido(fileArray);
			logVooRapido(resultado);
			fileArray = resultado.mantidas;
		}

		const totalFiles = fileArray.length;
		const bucketName = `temp-${Date.now()}`;
		const subfolders = this.groupFilesBySubfolder(fileArray);
		const subfolderEntries = Object.entries(subfolders);
		const subBucketNames = subfolderEntries
			.map(([fullPath]) => fullPath.split('/')[1])
			.filter(Boolean);
		const subfoldersArray = subfolderEntries.map(
			([_, folderFiles]) => folderFiles,
		);

		this.setState({
			status: 'uploading',
			progress: 0,
			totalFiles,
			completedFiles: 0,
			bucketName,
			subBucketNames,
			errorMessage: undefined,
		});
		this.showUploadToast();

		try {
			await firstValueFrom(
				this.uploadService
					.createBucket(bucketName)
					.pipe(takeUntil(this.cancelSignal$)),
			);

			let completedFiles = 0;

			const flatQueue: { file: File; subBucketName: string }[] = [];
			const filesBySubBucket = new Map<string, File[]>();
			for (let i = 0; i < subfoldersArray.length; i++) {
				const folderFiles = subfoldersArray[i];
				const subBucketName = subBucketNames[i];
				const existing = filesBySubBucket.get(subBucketName);
				filesBySubBucket.set(
					subBucketName,
					existing ? existing.concat(folderFiles) : folderFiles.slice(),
				);
				for (const file of folderFiles) {
					flatQueue.push({ file, subBucketName });
				}
			}

			const urlMapPromiseBySubBucket = new Map<
				string,
				Promise<Map<string, string>>
			>();
			const getUrlForFile = (
				file: File,
				subBucketName: string,
			): Promise<string> => {
				let mapPromise = urlMapPromiseBySubBucket.get(subBucketName);
				if (!mapPromise) {
					const folderFiles = filesBySubBucket.get(subBucketName) ?? [file];
					mapPromise = firstValueFrom(
						this.uploadService
							.getUrlFromStorage(
								folderFiles.map((f) => f.name),
								bucketName,
								subBucketName,
							)
							.pipe(takeUntil(this.cancelSignal$)),
					).then(({ urls }) => {
						const map = new Map<string, string>();
						folderFiles.forEach((f, idx) => map.set(f.name, urls[idx]));
						return map;
					});
					urlMapPromiseBySubBucket.set(subBucketName, mapPromise);
				}
				return mapPromise.then((map) => {
					const url = map.get(file.name);
					if (!url) {
						throw new Error(
							`URL assinada não retornada para ${file.name}`,
						);
					}
					return url;
				});
			};

			await runWithConcurrency(
				flatQueue,
				this.FOLDER_UPLOAD_CONCURRENCY,
				({ file, subBucketName }) => {
					if (this.cancelled) throw new Error('cancelled');
					return this.uploadFileWithRetry(
						bucketName,
						file,
						subBucketName,
						getUrlForFile,
					);
				},
				() => {
					completedFiles++;
					const pct = Math.round((completedFiles / totalFiles) * 100);
					this.setState({ completedFiles, progress: pct });
				},
			);

			if (this.cancelled) throw new Error('cancelled');

			this.setState({ status: 'processing' });

			const uploadElapsedSeconds = Math.max(
				0,
				Math.round((Date.now() - uploadStartMs) / 1000),
			);

			await firstValueFrom(
				this.uploadService
					.uploadImages(
						bucketName,
						this.userSession.userId() ?? '',
						this.userSession.farmId() ?? '',
						subBucketNames,
						uploadElapsedSeconds,
						dataLevantamento,
						modoDev,
					)
					.pipe(takeUntil(this.cancelSignal$)),
			);
			this.setState({ progress: 100, status: 'done' });

			this.notification.success(
				'Upload Completo',
				'Arquivos enviados e processamento iniciado.',
			);

			setTimeout(() => this.clearUploadToast(), 2000);
		} catch (e: any) {
			if (this.cancelled || e?.message === 'cancelled') {
				this.setState({
					status: 'cancelled',
					progress: 0,
					errorMessage: undefined,
				});
				this.notification.warn(
					'Upload cancelado',
					'Envio cancelado pelo usuário.',
				);
				this.clearUploadToast();
				return;
			}

			this.setState({
				status: 'error',
				errorMessage: e?.message ?? 'Erro no upload',
			});
			this.notification.error(
				'Erro de Upload',
				e?.message || 'Ocorreu um erro durante o upload.',
			);
		} finally {
			this.isSubmitting = false;
			this.cleanupSubs();
		}
	}

	private uploadFileWithRetry(
		bucketName: string,
		file: File,
		subBucketName: string,
		getUrl: (file: File, subBucketName: string) => Promise<string>,
		maxRetries = 3,
	): Promise<void> {
		const attempt = (
			retriesLeft: number,
			urlPromise: Promise<string>,
		): Promise<void> =>
			new Promise<void>((resolve, reject) => {
				if (this.cancelled) return reject(new Error('cancelled'));
				urlPromise
					.then((url) => {
						if (this.cancelled) return reject(new Error('cancelled'));
						const sub = this.uploadService
							.uploadImageStorage(file, url)
							.pipe(takeUntil(this.cancelSignal$))
							.subscribe({
								next: () => resolve(),
								complete: () => {

									if (this.cancelled) reject(new Error('cancelled'));
								},
								error: (err) => reject(err),
							});
						this.activeSubs.add(sub);
					})
					.catch(reject);
			}).catch(async (err) => {
				if (this.cancelled || err?.message === 'cancelled') throw err;

				const isNetworkError = err?.status === 0;
				if (retriesLeft > 0 && isNetworkError) {
					const backoffMs = 500 * (maxRetries - retriesLeft + 1);
					await new Promise((r) => setTimeout(r, backoffMs));
					const freshUrl = firstValueFrom(
						this.uploadService
							.getUrlFromStorage([file.name], bucketName, subBucketName)
							.pipe(takeUntil(this.cancelSignal$)),
					).then(({ urls }) => {
						if (!urls?.length) {
							throw new Error(
								`URL assinada não retornada para ${file.name}`,
							);
						}
						return urls[0];
					});
					return attempt(retriesLeft - 1, freshUrl);
				}
				throw err;
			});

		return attempt(maxRetries, getUrl(file, subBucketName));
	}

	cancel(): void {
		this.isSubmitting = false;
		const bucketName = this.getState().bucketName;
		if (bucketName) {
			// Sem takeUntil: o cancelSignal$ dispara logo abaixo e abortaria
			// este próprio pedido antes de ele chegar ao backend.
			this.uploadService
				.cancelProcessing({ bucketName })
				.subscribe({
					error: () => {

					},
				});
		}

		this.cancelled = true;
		this.cancelSignal$.next();
		this.cancelSignal$.complete();
		this.cleanupSubs();
		this.setState({
			status: 'cancelled',
			progress: 0,
			errorMessage: undefined,
		});
		this.clearUploadToast();
	}

	private setState(patch: Partial<UploadState>): void {
		this.stateSubject.next({ ...this.stateSubject.value, ...patch });
	}

	private showUploadToast(): void {
		this.notification.showProgress(this.toastKey, 'Upload em andamento', {
			detail: 'Enviando arquivos...',
			data: { type: 'upload-progress' },
		});
	}

	private clearUploadToast(): void {
		this.notification.clearProgress(this.toastKey);
	}

	private cleanupSubs(): void {
		for (const s of this.activeSubs) s.unsubscribe();
		this.activeSubs.clear();
	}

	private groupFilesBySubfolder(files: File[]): Record<string, File[]> {
		const subfolders: Record<string, File[]> = {};
		files.forEach((file) => {
			const path = file.webkitRelativePath || file.name;
			const parts = path.split('/');
			const folderKey =
				parts.length >= 2 ? `${parts[0]}/${parts[1]}` : parts[0];
			if (!subfolders[folderKey]) subfolders[folderKey] = [];
			subfolders[folderKey].push(file);
		});
		return subfolders;
	}
}
